import { common as c } from '@pendo/aggregations';

// ---------------------------------------------------------------------------
// Enums (sourced from analytics.types.ts)
// ---------------------------------------------------------------------------

export type MetadataValueType = 'boolean' | 'string' | 'integer' | 'float' | 'number' | 'aggregation' | 'list' | 'date';

export enum EventKind {
    Page = 'Page',
    Feature = 'Feature',
    TrackEvent = 'TrackType',
    Guide = 'Guide',
    AIAgent = 'AiAgent',
}

export enum EventName {
    Page = 'pageViews',
    Feature = 'featureClicks',
    TrackEvent = 'trackEvents',
    Guide = 'guideViews',
    App = 'anyActivity',
    AIAgent = 'aiAgentInteractions',
}

export enum TimeInterval {
    Hour = 'hour',
    Day = 'day',
    Week = 'week',
    Month = 'month',
}

export enum TimeSeriesPeriod {
    Hourly = 'hourly',
    HourlyRange = 'hourRange',
    Daily = 'daily',
    DailyRange = 'dayRange',
    Weekly = 'weekly',
    WeeklyRange = 'weekRange',
    Monthly = 'monthly',
    MonthlyRange = 'monthRange',
}

export enum DateConfigTypes {
    Today = 'Today',
    Yesterday = 'Yesterday',
    Last7Days = 'Last 7 Days',
    Last30Days = 'Last 30 Days',
    Last90Days = 'Last 90 Days',
    Last180Days = 'Last 180 Days',
    Custom = 'Custom',
}

export enum FilterOperator {
    True = 'is true',
    False = 'is false',
    Equal = '=',
    NotEqual = '!=',
    LessOrEqual = '<=',
    GreaterOrEqual = '>=',
    Contains = '~',
    NotContains = '!~',
    Empty = '()',
    NotEmpty = '(x)',
}

export enum FilterType {
    Device = 'device',
    Custom = 'custom',
    HistoricalMetadata = 'historicalMetadata',
    Utm = 'utm',
    Visitor = 'visitor',
    Account = 'account',
    ParentAccount = 'parentAccount',
    EventTopLevel = 'eventTopLevel',
    Location = 'location',
    PageParameter = 'parameter',
}

export enum MetadataTypeId {
    Automatic = 'automatic',
    Visitor = 'visitor',
    Account = 'account',
    ParentAccount = 'parentAccount',
    Historical = 'historical',
    Event = 'event',
    Utm = 'utm',
    PageParameter = 'pageParameter',
    EventTopLevel = 'eventTopLevel',
}

export const PageParameterId = 'parameters.parameter';

export const metadataTypeByFilterType: Record<string, MetadataTypeId> = {
    [FilterType.Device]: MetadataTypeId.Automatic,
    [FilterType.Custom]: MetadataTypeId.Event,
    [FilterType.HistoricalMetadata]: MetadataTypeId.Historical,
    [FilterType.Visitor]: MetadataTypeId.Visitor,
    [FilterType.Account]: MetadataTypeId.Account,
    [FilterType.ParentAccount]: MetadataTypeId.ParentAccount,
    [FilterType.Utm]: MetadataTypeId.Utm,
    [FilterType.EventTopLevel]: MetadataTypeId.EventTopLevel,
    [FilterType.Location]: MetadataTypeId.EventTopLevel,
    [FilterType.PageParameter]: MetadataTypeId.PageParameter,
};

// ---------------------------------------------------------------------------
// Metadata types
// ---------------------------------------------------------------------------

export interface MetadataValue {
    Type: MetadataValueType;
    DisplayName: string;
    ElementType?: string;
    ElementFormat?: string;
    Dirty?: boolean;
    isHidden?: boolean;
    isDeleted?: boolean;
    isCalculated?: boolean;
    isPerApp?: boolean;
    neverIndex?: boolean;
    isPromoted?: boolean;
    group?: string;
    field: string;
    kind: MetadataTypeId;
    sortable?: boolean;
    label?: string;
    prop?: string;
    isUtm?: boolean;
    id?: string;
    name?: string;
    selectionState?: string;
    isDisabled?: boolean;
    sample?: string | boolean | number;
    cardinality?: number;
    uniqueValues?: Array<string> | Array<number> | Array<Array<string>>;
}

export type DXMetadataValueType = Exclude<MetadataValueType, 'list' | 'date'>;

export interface DXMetadataValue extends MetadataValue {
    Type: DXMetadataValueType;
}

// ---------------------------------------------------------------------------
// Data Explorer domain types
// ---------------------------------------------------------------------------

export enum DxAggType {
    Iterative = 'iterative',
    Aggregate = 'aggregate',
    Formula = 'formula',
    Drilldown = 'drilldown',
    CSV = 'csv',
}

export type DxTargetMeasureName =
    | 'total'
    | 'numMinutes'
    | 'rageClicks'
    | 'deadClicks'
    | 'uTurns'
    | 'errorClicks'
    | 'averageMinutesPerVisitor'
    | 'averageMinutesPerAccount'
    | 'average'
    | 'averageAccounts'
    | 'median'
    | 'numVisitors'
    | 'numAccounts'
    | 'percentVisitors'
    | 'percentAccounts';

export interface DxTargetMeasure {
    name: DxTargetMeasureName;
}

export interface Validatable<T> {
    valid: boolean;
    value: T;
}

// ---------------------------------------------------------------------------
// Segment types
// ---------------------------------------------------------------------------

export interface DxSegment {
    default?: boolean;
    disabled?: boolean | null;
    id: string;
    name?: string;
    compound?: string;
    pipeline?: c.PipelineData[];
    definition?: {
        advancedOptions: Record<string, string | null | boolean>;
        filters: Array<{
            group: string;
            kind: string;
            field: string;
            id: string;
            type: string;
            name: string;
            schema: string;
            elementType: string;
            operator: string;
            value: string | Array<string>;
        }>;
    };
    pipelines?: Record<string, Array<c.PipelineData>>;
}

export interface DxSegmentConfig {
    valid: boolean;
    value: DxSegment;
}

// ---------------------------------------------------------------------------
// Date / time series types
// ---------------------------------------------------------------------------

export type TimeSeriesInterval = 'hour' | 'day' | 'week' | 'month';

export interface DxDateRange {
    dateRange: {
        type: string;
        start?: string | number;
        end?: string | number;
        key?: string;
        offset?: number;
    };
    period?: c.TimeSeriesPeriodType;
    interval?: TimeSeriesInterval;
}

export interface DxDateConfig {
    valid: boolean;
    isPrimary: boolean;
    value: DxDateRange;
}

export interface DxTimeSeries {
    period: TimeSeriesPeriod;
    first: string | number;
    last?: string | number;
    count?: number;
}

// ---------------------------------------------------------------------------
// Event / target types
// ---------------------------------------------------------------------------

export interface DxEvent {
    id: string | number;
    appId?: number;
    kind?: EventKind;
}

export type DxSourceEventType =
    | 'trackEvents'
    | 'pageEvents'
    | 'featureEvents'
    | 'guideEvents'
    | 'events'
    | 'aiAgentInteractions';

export interface DxEventType {
    name: EventName;
    label: string;
    title?: string;
    timestampLabel: string;
    moduleName: string;
    sourceType: string;
    sourceEvents: DxSourceEventType;
    sourceId: string;
    supplementalFilters: Array<c.PipelineMerge | c.PipelineFilter | c.PipelineEval | c.InvalidOperator>;
    isAnyActivity?: boolean;
}

// ---------------------------------------------------------------------------
// Measure types
// ---------------------------------------------------------------------------

export interface DxMeasure {
    kind: 'visitor' | 'account' | null;
    timeIntervalGroupFields(config: { includeSourceId: boolean }): c.PipelineGroupField[];
    needsMerge: boolean;
    selectColumns: Record<string, string>;
    yAxis: string;
    isMedian?: boolean;
    isVisitorOrAccountPercent?: boolean;
    postGroupEval?(subgroup?: string): c.PipelineEval | c.InvalidOperator;
    groupingSelectColumns?: string[];
    mergeSelectColumns?: string[];
    sortColumns(config: { includeSourceId?: boolean }): string[];
}

// ---------------------------------------------------------------------------
// Filter types
// ---------------------------------------------------------------------------

export interface DxFilter {
    id: string;
    field: string;
    kind: MetadataTypeId;
    filterType: FilterType;
    operator: FilterOperator;
    comparisonValues: string[];
    group?: string;
    name?: string;
}

// ---------------------------------------------------------------------------
// Grouping types
// ---------------------------------------------------------------------------

export interface DxGroupBy {
    id: MetadataTypeId;
    metadataGroup(metadata: DXMetadataValue | MetadataValue): string;
    bulkExpand: c.PipelineBulkExpand | c.InvalidOperator | null;
    evalExpression: c.PipelineEval | c.InvalidOperator | null;
    trimExpression?(fieldName: string): string;
    userAgent?: c.PipelineUserAgent;
}

export interface DxGrouping {
    groupBy: DxGroupBy;
    groupByMetadata: DXMetadataValue;
}

export interface DxGroupConfig {
    configuration: DXMetadataValue;
    type: MetadataTypeId;
}

// ---------------------------------------------------------------------------
// Target types
// ---------------------------------------------------------------------------

export interface DxTarget {
    filters?: DxFilter[];
    id: string | number;
    type: EventName;
}

export interface DxTargetGroup {
    id: Validatable<string>;
    name?: Validatable<string>;
    measure: Validatable<DxTargetMeasure>;
    targets: DxTarget[];
}

// ---------------------------------------------------------------------------
// Account types
// ---------------------------------------------------------------------------

export interface DxAccount {
    id: string;
    text?: string;
    isParentAccount: boolean;
}

// ---------------------------------------------------------------------------
// Formula types
// ---------------------------------------------------------------------------

export type DxFormulaMeasure = 'total' | 'visitor' | 'accounts';
export type DxFormulaFormat = 'percent' | 'decimal';
export type DxFormulaTypeId = 'sum' | 'ratio' | 'relativePercentage';

interface SumFormula {
    id: string;
    type: 'sum';
    groupId: string;
    name?: string;
    invalid?: string;
}

interface RatioFormula {
    id: string;
    type: 'ratio';
    numeratorGroupId: string;
    denominatorGroupId: string;
    name?: string;
    format: DxFormulaFormat;
    invalid?: string;
}

interface RelativePercentageFormula {
    id: string;
    type: 'relativePercentage';
    numeratorGroupId: string;
    denominatorGroupId: string;
    name?: string;
    format: DxFormulaFormat;
    invalid?: string;
}

export type DxFormula = SumFormula | RatioFormula | RelativePercentageFormula;

// ---------------------------------------------------------------------------
// Drilldown types
// ---------------------------------------------------------------------------

export interface DxDrilldownIdentifiers {
    visitorIdentifier: string;
    accountIdentifier: string;
}

export interface DxDrilldownTarget {
    id: string;
    groupValue0?: string | boolean | number | null;
    groupValue1?: string | boolean | number | null;
    groupValue2?: string | boolean | number | null;
    groupValue3?: string | boolean | number | null;
    groupValue4?: string | boolean | number | null;
    type: {
        name: string;
    };
}

// ---------------------------------------------------------------------------
// Resolved metadata input type
//
// Pre-resolved by the caller (e.g., Vue layer) so the middleware stays
// stateless and does not depend on vuex, device-props, or filterEventPropList.
// ---------------------------------------------------------------------------

/**
 * A map from filter identifier string to the metadata value it represents.
 * The caller is responsible for resolving device props, event properties,
 * UTM properties, and historical metadata into this flat lookup map.
 *
 * Keys are produced by `getFilterIdentifier()` in utils.ts.
 */
export type ResolvedFilterProperties = Record<string, MetadataValue>;

// ---------------------------------------------------------------------------
// Build aggregations input / output — the main middleware API surface
// ---------------------------------------------------------------------------

export interface BuildAggregationsInput {
    targetGroups: DxTargetGroup[];
    targetEntityMap?: Record<string, Record<string, DxEvent>>;

    // Query globals — flat, not nested
    segments: DxSegmentConfig[];
    dateConfigs: DxDateConfig[];
    groupings: DxGroupConfig[];
    filters: DxFilter[];
    formulas: DxFormula[];
    account: DxAccount;

    // Pre-resolved metadata
    resolvedFilterProperties: ResolvedFilterProperties;

    // Options / feature flags
    hideFormulas?: boolean;
    hasAccountLevelAnalyticsEnabled?: boolean;
    hasShowAnonymousFlag?: boolean;
    maxCardinalityCount?: number;
    restrictResults?: boolean;
}

export interface IterativeAggConfig {
    targetGroupId: string;
    targetId: string | number | number[] | string[];
    typeId: string;
    multipleTargets: boolean;
    aggType: string;
    globalId?: string | null;
    agg: {
        name: string;
        displayName?: string;
        pipeline: c.PipelineData[];
        [key: string]: unknown;
    };
}

export interface FormulaAggConfig {
    aggType: DxAggType;
    responseFormat: string;
    targetGroupId: string;
    agg: {
        name: string;
        pipeline: c.PipelineData[];
        [key: string]: unknown;
    };
}

export interface BuildAggregationsOutput {
    iterativeAggregations: IterativeAggConfig[];
    formulaAggregations: FormulaAggConfig[];
    timeSeriesConfigs: DxTimeSeries[];
}
